import { Component, EventEmitter, OnDestroy, OnInit, Output, ViewChild, ElementRef, HostListener, ChangeDetectorRef, ChangeDetectionStrategy, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NgForm } from '@angular/forms';
import { RelatedPartyPaymentService } from '../../services/related-party-payment.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
  hydratedGlobalParams,
} from '../../../shared/workspace-element-context';

const US_FOREIGN_INDICATOR_LIST = [
  { code: 'F', label: 'Foreign' },
  { code: 'U', label: 'US' }
];

@Component({
    selector: 'gtw-related-party-payment-add-ce',
    templateUrl: './related-party-payment-add.component.html',
    styleUrls: ['./related-party-payment-add.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class RelatedPartyPaymentAddComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();
  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();
  @ViewChild('rppForm') rppForm!: NgForm;
  @ViewChild('payorSearchInput') payorSearchInput!: ElementRef;
  @ViewChild('recipientSearchInput') recipientSearchInput!: ElementRef;

  // Standard component properties
  modalTitle = "Add New Payment";
  minimise = false;
  crudLoading = false;

  // Data properties
  rowData: any = {};
  filterParams: any = {};
  globalParams: any = {};
  GENERAL_CONFIG: any = {};
  USER_SETTINGS: any = {};
  
  // Form data
  paymentTypes: any[] = [];
  payorList: any[] = [];
  recipientList: any[] = [];
  usForeignIndList = US_FOREIGN_INDICATOR_LIST;
  
  // Filtered lists for search
  filteredPayorList: any[] = [];
  filteredRecipientList: any[] = [];
  payorFilteredTotal: number = 0;  // Total matching search
  recipientFilteredTotal: number = 0;  // Total matching search
  
  // Full filtered lists (before slicing for display)
  private payorFullFiltered: any[] = [];
  private recipientFullFiltered: any[] = [];
  
  // Search texts
  payorSearchText: string = '';
  recipientSearchText: string = '';
  
  // Dropdown open/close state
  payorDropdownOpen: boolean = false;
  recipientDropdownOpen: boolean = false;
  
  // Lazy load batch size
  private readonly LAZY_LOAD_SIZE = 10;

  // Lazy load objects
  lazyLoadPayor: any = { totalDrpList: [], cachedDrpList: [] };
  lazyLoadRecipient: any = { totalDrpList: [], cachedDrpList: [] };
  lazyLoadUsForeignInd: any = { 
    totalDrpList: US_FOREIGN_INDICATOR_LIST, 
    cachedDrpList: US_FOREIGN_INDICATOR_LIST 
  };

  // Loading states
  loadingPayor = false;
  loadingRecipient = false;

  // Form fields
  selectedPayor: any = null;
  selectedRecipient: any = null;
  selectedPaymentType: any = null;
  selectedUsForeignInd: any = null;
  dateSelected: Date | string | null = null;  // String from <input type="date"> (YYYY-MM-DD) or Date object
  amountPaid: number = 0;
  amountPaid2: number = 0; // USD
  amountReceived: number = 0;
  amountReceived2: number = 0; // USD
  withholdingTax: number = 0;
  cashPayment: string = '';
  countryEligible: string = '';
  taxBaseVal: number = 0;
  taxBaseValUsd: number = 0;

  // Validation flags
  dateFlag = false;
  usForeignIndRequired = false;
  invalidTaxBaseVal = false;
  payorRequired = false;
  recipientRequired = false;

  // Calendar
  calendar = { opened: false };

  constructor(
    private service: RelatedPartyPaymentService,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    console.log('🎬 PaymentAdd: Component initialized');
    this.applyWorkspaceContext();
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
        this.init();
      })
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
        this.init();
      })
    );
    this.setupAmountReceivedWatch();
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    const baseUrl = this.GENERAL_CONFIG?.base_url;
    if (baseUrl) {
      this.service.setBaseUrl(baseUrl);
    }
    this.USER_SETTINGS = header.userSettingsData || {};
    this.globalParams = header.globalParams || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(rawRow) ? (rawRow[0] && typeof rawRow[0] === 'object' ? rawRow[0] : {}) : (rawRow || {});
    this.cdr.markForCheck();
  }

  init(): void {
    console.log('🔄 PaymentAdd: init() called, rowData:', this.rowData);
    this.loadPaymentTypes();
    this.loadPayors();
    this.loadRecipients();
  }

  /**
   * Setup watcher for amount received (must be negative)
   */
  private setupAmountReceivedWatch(): void {
    // Angular reactive way would use form valueChanges
    // For now, we'll handle it in the validate method
  }

  /**
   * Load payment types
   */
  loadPaymentTypes(): void {
    this.service.getPaymentTypes(this.rowData, this.globalParams).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any[]) => {
        // Service already maps to jsonObject, so data is the array directly
        this.paymentTypes = data || [];
        console.log('Payment types loaded:', this.paymentTypes.length, 'types');
        this.cdr.markForCheck();
      },
      (error) => {
        console.error('Error loading payment types:', error);
        this.showError(error.message || 'Error loading payment types');
        this.cdr.markForCheck();
      }
    );
  }

  /**
   * Load payors (lazy load)
   */
  loadPayors(): void {
    const taxYear = this.filterParams.tax_year || this.globalParams.tax_year;
    
    if (!taxYear) {
      this.showError('Tax year not available');
      return;
    }
    
    this.service.getPayor(taxYear, this.globalParams).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any[]) => {
        // Service already maps to jsonObject, so data is the array directly
        this.payorList = data || [];
        // Initialize lazy loading
        this.payorFullFiltered = this.payorList;
        this.payorFilteredTotal = this.payorList.length;
        this.filteredPayorList = this.payorList.slice(0, this.LAZY_LOAD_SIZE);
        this.lazyLoadPayor.totalDrpList = this.payorList;
        this.lazyLoadPayor.cachedDrpList = this.filteredPayorList;
        this.loadingPayor = true;
        console.log('Payors loaded:', this.payorList.length, 'payors');
        this.cdr.markForCheck();
      },
      (error) => {
        console.error('Error loading payors:', error);
        this.showError(error.message || 'Error loading payors');
        this.cdr.markForCheck();
      }
    );
  }
  
  /**
   * Filter payor list based on search text (with lazy loading)
   */
  filterPayor(): void {
    if (!this.payorSearchText) {
      // No search - show first batch
      this.payorFullFiltered = this.payorList;
      this.payorFilteredTotal = this.payorList.length;
      this.filteredPayorList = this.payorList.slice(0, this.LAZY_LOAD_SIZE);
      return;
    }
    
    // Search and filter
    const searchLower = this.payorSearchText.toLowerCase();
    this.payorFullFiltered = this.payorList.filter(p => 
      (p.PAYOR && p.PAYOR.toLowerCase().includes(searchLower)) ||
      (p.LEID && p.LEID.toString().includes(searchLower)) ||
      (p.CDR_NO && p.CDR_NO.toString().includes(searchLower))
    );
    
    this.payorFilteredTotal = this.payorFullFiltered.length;
    this.filteredPayorList = this.payorFullFiltered.slice(0, this.LAZY_LOAD_SIZE);
  }
  
  /**
   * Load more payors on scroll
   */
  onPayorScroll(event: Event): void {
    const target = event.target as HTMLSelectElement;
    const scrollTop = target.scrollTop;
    const scrollHeight = target.scrollHeight;
    const clientHeight = target.clientHeight;
    
    // Load more when near bottom (80% scrolled)
    if (scrollTop + clientHeight >= scrollHeight * 0.8) {
      const currentLength = this.filteredPayorList.length;
      if (currentLength < this.payorFilteredTotal) {
        // Load next batch
        const nextBatch = this.payorFullFiltered.slice(currentLength, currentLength + this.LAZY_LOAD_SIZE);
        this.filteredPayorList = [...this.filteredPayorList, ...nextBatch];
      }
    }
  }

  /**
   * Load recipients (lazy load)
   */
  loadRecipients(): void {
    const taxYear = this.filterParams.tax_year || this.globalParams.tax_year;
    
    if (!taxYear) {
      this.showError('Tax year not available');
      return;
    }
    
    this.service.getRecipients(taxYear, this.globalParams).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any[]) => {
        // Service already maps to jsonObject, so data is the array directly
        this.recipientList = data || [];
        // Initialize lazy loading
        this.recipientFullFiltered = this.recipientList;
        this.recipientFilteredTotal = this.recipientList.length;
        this.filteredRecipientList = this.recipientList.slice(0, this.LAZY_LOAD_SIZE);
        this.lazyLoadRecipient.totalDrpList = this.recipientList;
        this.lazyLoadRecipient.cachedDrpList = this.filteredRecipientList;
        this.loadingRecipient = true;
        console.log('Recipients loaded:', this.recipientList.length, 'recipients');
        this.cdr.markForCheck();
      },
      (error) => {
        console.error('Error loading recipients:', error);
        this.showError(error.message || 'Error loading recipients');
        this.cdr.markForCheck();
      }
    );
  }
  
  /**
   * Filter recipient list based on search text (with lazy loading)
   */
  filterRecipient(): void {
    if (!this.recipientSearchText) {
      // No search - show first batch
      this.recipientFullFiltered = this.recipientList;
      this.recipientFilteredTotal = this.recipientList.length;
      this.filteredRecipientList = this.recipientList.slice(0, this.LAZY_LOAD_SIZE);
      return;
    }
    
    // Search and filter
    const searchLower = this.recipientSearchText.toLowerCase();
    this.recipientFullFiltered = this.recipientList.filter(r => 
      (r.RECIPIENT && r.RECIPIENT.toLowerCase().includes(searchLower)) ||
      (r.LEID && r.LEID.toString().includes(searchLower)) ||
      (r.CDR_NO && r.CDR_NO.toString().includes(searchLower))
    );
    
    this.recipientFilteredTotal = this.recipientFullFiltered.length;
    this.filteredRecipientList = this.recipientFullFiltered.slice(0, this.LAZY_LOAD_SIZE);
  }
  
  /**
   * Load more recipients on scroll
   */
  onRecipientScroll(event: Event): void {
    const target = event.target as HTMLSelectElement;
    const scrollTop = target.scrollTop;
    const scrollHeight = target.scrollHeight;
    const clientHeight = target.clientHeight;
    
    // Load more when near bottom (80% scrolled)
    if (scrollTop + clientHeight >= scrollHeight * 0.8) {
      const currentLength = this.filteredRecipientList.length;
      if (currentLength < this.recipientFilteredTotal) {
        // Load next batch
        const nextBatch = this.recipientFullFiltered.slice(currentLength, currentLength + this.LAZY_LOAD_SIZE);
        this.filteredRecipientList = [...this.filteredRecipientList, ...nextBatch];
      }
    }
  }

  /**
   * Validate and convert amount to USD
   */
  validateAmt(val: number, wtdAvgRate: number, objType: string, fieldToUpdate: string, fieldName: string): void {
    // Reset validation
    this.invalidTaxBaseVal = false;

    // Tax Base Value validation - must be non-negative
    if (fieldName === 'tax_base_val' && val < 0) {
      console.log('Invalid tax base value:', val);
      this.invalidTaxBaseVal = true;
      return;
    }

    // Convert to USD
    this.convertToUS(val, wtdAvgRate, objType, fieldToUpdate, fieldName);
  }

  /**
   * Convert amount to USD using WTD Average Rate
   */
  private convertToUS(val: number, wtdAvgRate: number, objType: string, fieldToUpdate: string, fieldName: string): void {
    if (objType === 'PAIDOBJ') {
      this.amountPaid2 = Math.round(val * wtdAvgRate);
    } else if (objType === 'RECEIVEDOBJ') {
      this.amountReceived2 = Math.round(val * wtdAvgRate);
    } else if (objType === 'CASHDISTOBJ') {
      this.taxBaseValUsd = Math.round(val * wtdAvgRate);
    }
  }

  /**
   * Make Amount Received negative
   */
  makeAmountReceivedNegative(): void {
    if (this.amountReceived && this.amountReceived > 0) {
      // Convert to negative
      this.amountReceived = -Math.abs(this.amountReceived);
      
      // Update USD field as well
      const wtdAvgRate = this.selectedRecipient?.WTD_AVG_RATE || 1;
      this.amountReceived2 = Math.round(this.amountReceived * wtdAvgRate);
    }
  }

  /**
   * Format and validate date
   */
  formatDate(): void {
    // Reset flag first
    this.dateFlag = false;
    
    if (!this.dateSelected) {
      this.dateFlag = true;
      return;
    }

    const taxYear = parseInt(this.filterParams.tax_year || this.globalParams.tax_year || '2019');
    
    // Handle string date from <input type="date"> (format: YYYY-MM-DD)
    let selectedYear: number;
    
    try {
      if (typeof this.dateSelected === 'string') {
        // Extract year from string (YYYY-MM-DD)
        const parts = this.dateSelected.split('-');
        if (parts.length >= 1 && parts[0]) {
          selectedYear = parseInt(parts[0]);
        } else {
          this.dateFlag = true;
          return;
        }
      } else if (this.dateSelected instanceof Date) {
        // Handle Date object
        selectedYear = this.dateSelected.getFullYear();
      } else {
        this.dateFlag = true;
        return;
      }
      
      // Validate year
      if (isNaN(selectedYear)) {
        this.dateFlag = true;
        return;
      }

      if (selectedYear > taxYear) {
        this.dateFlag = true;
      } else {
        this.dateFlag = false;
      }
    } catch (error) {
      console.error('Error formatting date:', error);
      this.dateFlag = true;
    }
  }

  /**
   * Validate US Foreign Indicator
   */
  validateUsForeignIndicator(): boolean {
    this.usForeignIndRequired = !(this.selectedUsForeignInd && this.selectedUsForeignInd.code);
    return !this.usForeignIndRequired;
  }

  /**
   * Open calendar
   */
  openCalendar(): void {
    this.calendar.opened = true;
  }

  /**
   * Handle payment type change - set default cashPayment only if not already set by user
   */
  onPaymentTypeChange(): void {
    if (this.selectedPaymentType && !this.cashPayment) {
      this.cashPayment = this.selectedPaymentType.PAYMENT_TYPE === 'DIVIDEND' ? 'Y' : 'N';
    }
  }

  /**
   * Explicitly set cash payment value from radio button click
   */
  setCashPayment(value: string): void {
    this.cashPayment = value;
    console.log('Cash payment set to:', this.cashPayment);
  }

  /**
   * Handle country eligible change
   */
  countryEligibleChange(): void {
    console.log('Country eligible changed:', this.countryEligible);
    // Additional logic if needed
  }

  /**
   * Print selected payor (debug)
   */
  printSelectedPayor(): void {
    console.log('Selected payor:', this.selectedPayor);
  }

  /**
   * Print selected recipient (debug)
   */
  printSelectedRecipient(): void {
    console.log('Selected recipient:', this.selectedRecipient);
  }

  /**
   * Check if all required fields are filled for enabling amount fields
   */
  disableAllFields(): boolean {
    return !this.selectedPayor || !this.selectedRecipient || !this.selectedPaymentType || !this.dateSelected;
  }

  /**
   * Check if amount paid field should be disabled
   */
  isAmtPaidDisabled(): boolean {
    return this.disableAllFields();
  }

  /**
   * Check if amount received field should be disabled
   */
  isAmtReceivedDisabled(): boolean {
    return this.disableAllFields();
  }

  /**
   * Check if withholding tax field should be disabled
   */
  isWithholdingTaxDisabled(): boolean {
    return this.disableAllFields();
  }

  /**
   * Validate Payor
   */
  validatePayor(): boolean {
    this.payorRequired = !this.selectedPayor;
    return !this.payorRequired;
  }

  /**
   * Validate Recipient
   */
  validateRecipient(): boolean {
    this.recipientRequired = !this.selectedRecipient;
    return !this.recipientRequired;
  }

  /**
   * Save new payment
   */
  saveNewPayment(): void {
    // Validate Payor
    if (!this.validatePayor()) {
      this.showError('Please select Payor');
      return;
    }

    // Validate Recipient
    if (!this.validateRecipient()) {
      this.showError('Please select Recipient');
      return;
    }

    // Validate date
    this.formatDate();
    if (this.dateFlag || !this.dateSelected) {
      this.dateFlag = true;
      this.showError('Please enter valid date');
      return;
    }

    // Validate US Foreign Indicator
    if (!this.validateUsForeignIndicator()) {
      this.showError('Please select US Foreign Indicator');
      return;
    }

    // Validate tax base val if cash payment is N
    if (this.cashPayment === 'N' && this.invalidTaxBaseVal) {
      this.showError('Tax basis amount should not be negative');
      return;
    }

    // Build payment JSON
    const paymentDate = this.formatDateForSave(this.dateSelected);
    const selectedRecp = this.selectedRecipient || {};
    const selectedPayor = this.selectedPayor || {};

    const cashPaymentValue = this.cashPayment || (this.selectedPaymentType.PAYMENT_TYPE === "DIVIDEND" ? 'Y' : 'N');
    console.log('💾 PaymentAdd SAVE: cashPayment property =', this.cashPayment, '→ sending cash_payment =', cashPaymentValue);

    const paymentJson = [{
      "tax_year": this.filterParams.tax_year || this.globalParams.tax_year,
      "payor_leid": selectedPayor.LEGAL_ENTITY_ID,
      "payor_cdr_no": selectedPayor.GECC_ENTITY_ID,
      "filing_group": this.globalParams.filing_key || this.filterParams.filing_key || 1,
      "scenario": this.filterParams.scenario || this.globalParams.scenario,
      "recipient_leid": selectedRecp.LEGAL_ENTITY_ID,
      "recipient_cdr_no": selectedRecp.GECC_ENTITY_ID,
      "payment_type": this.selectedPaymentType.PAYMENT_TYPE,
      "include": "Y",
      "payment_source": "M",
      "payment_date": paymentDate,
      "cash_payment": cashPaymentValue,
      "amt_paid": this.amountPaid,
      "amt_received": this.amountReceived,
      "tax_amt": this.withholdingTax,
      "us_foreign_ind": this.selectedUsForeignInd.code,
      "same_country": this.countryEligible === '' ? 'N' : this.countryEligible,
      "payment_sub_type": this.selectedPaymentType.PAYMENT_SUB_TYPE,
      "ho_cpaymentey": this.rowData.HO_COMBINATION_KEY,
      "tax_basis_amt": this.taxBaseVal
    }];

    const clobSettings = [{
      tax_year: this.filterParams.tax_year || this.globalParams.tax_year,
      sso_id: this.USER_SETTINGS.user?.sso_id,
      screen_key: this.globalParams.screen_key
    }];

    const ssoId = this.USER_SETTINGS.user?.sso_id;

    // Check lock obj
    const checkLockObj = [{ consol_group_key: this.filterParams.filing_key }];

    console.log('Saving new payment:', paymentJson);

    this.crudLoading = true;

    this.service.addNewPayment(paymentJson, clobSettings, ssoId, checkLockObj, this.globalParams, this.filterParams).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.crudLoading = false;

        if (response.data?.errorMessage && response.data.errorMessage !== "null") {
          this.showError(response.data.errorMessage);
        } else {
          this.showSuccess('The new Recipient was successfully added');
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.markForCheck();
      },
      (error) => {
        this.crudLoading = false;
        console.error('Error saving payment:', error);
        this.showError('Error saving payment');
        this.cdr.markForCheck();
      }
    );
  }

  /**
   * Format date for save (MM/dd/yyyy)
   */
  private formatDateForSave(date: Date | string | null): string {
    if (!date) return '';
    
    // Handle string date from <input type="date"> (format: YYYY-MM-DD)
    if (typeof date === 'string') {
      const parts = date.split('-');
      if (parts.length === 3) {
        const year = parts[0];
        const month = parts[1];
        const day = parts[2];
        return `${month}/${day}/${year}`;
      }
      return '';
    }
    
    // Handle Date object
    if (date instanceof Date) {
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${month}/${day}/${year}`;
    }
    
    return '';
  }

  /**
   * Cancel and close modal
   */
  cancel(): void {
    this.crudLoading = false;
    this.requestGridUpdate.emit('cancel');
  }

  /**
   * Show success message
   */
  private showSuccess(message: string): void {
    this.snackBar.open(message, 'Close', {
      duration: 4000,
      panelClass: ['success-snackbar']
    });
  }

  /**
   * Show error message
   */
  private showError(message: string): void {
    this.snackBar.open(message, 'Close', {
      duration: 4000,
      panelClass: ['error-snackbar']
    });
  }

  /**
   * Toggle Payor dropdown
   */
  togglePayorDropdown(): void {
    this.payorDropdownOpen = !this.payorDropdownOpen;
    this.recipientDropdownOpen = false;  // Close other dropdown
    
    if (this.payorDropdownOpen) {
      // Focus search input after dropdown opens
      setTimeout(() => {
        if (this.payorSearchInput && this.payorSearchInput.nativeElement) {
          this.payorSearchInput.nativeElement.focus();
        }
      }, 100);
    }
  }

  /**
   * Toggle Recipient dropdown
   */
  toggleRecipientDropdown(): void {
    this.recipientDropdownOpen = !this.recipientDropdownOpen;
    this.payorDropdownOpen = false;  // Close other dropdown
    
    if (this.recipientDropdownOpen) {
      // Focus search input after dropdown opens
      setTimeout(() => {
        if (this.recipientSearchInput && this.recipientSearchInput.nativeElement) {
          this.recipientSearchInput.nativeElement.focus();
        }
      }, 100);
    }
  }

  /**
   * Select a payor from dropdown
   */
  selectPayor(payor: any): void {
    this.selectedPayor = payor;
    this.payorRequired = false; // Clear validation error when selected
    this.printSelectedPayor();
    this.payorDropdownOpen = false;
    // Reset search
    this.payorSearchText = '';
    this.filterPayor();
  }

  /**
   * Select a recipient from dropdown
   */
  selectRecipient(recipient: any): void {
    this.selectedRecipient = recipient;
    this.recipientRequired = false; // Clear validation error when selected
    this.printSelectedRecipient();
    this.recipientDropdownOpen = false;
    // Reset search
    this.recipientSearchText = '';
    this.filterRecipient();
  }

  /**
   * Close dropdowns when clicking outside
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    
    // Check if click is outside dropdown
    if (!target.closest('.custom-dropdown-wrapper')) {
      this.payorDropdownOpen = false;
      this.recipientDropdownOpen = false;
    }
  }
}

